import { useState } from "react";
import { Truck, HelpCircle } from "lucide-react";
import { DemoHint } from "@/components/demo/DemoHint";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface PostLoadHeaderProps {
  isEditing?: boolean;
  title?: string;
  isDirty?: boolean;
}

export default function PostLoadHeader({ isEditing, title, isDirty }: PostLoadHeaderProps) {
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-card border-b border-border">
      <div className="flex items-center gap-2.5">
        <Truck className="h-4.5 w-4.5" style={{ color: "hsl(var(--brand-teal-deep))" }} />
        <DemoHint hintKey="page.postLoad" side="bottom" showIndicator>
          <h2 className="text-base font-semibold tracking-wide uppercase text-foreground">
            {title || (isEditing ? "Edit Load" : "Quick Post")}
          </h2>
        </DemoHint>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <Dialog>
          <DialogTrigger asChild>
            <span className="cursor-pointer hover:underline text-muted-foreground flex items-center gap-1">
              <HelpCircle className="h-3 w-3" />
              Help
            </span>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>How to Post a Load</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div>
                <h4 className="font-semibold text-foreground mb-1">1. Add Stops</h4>
                <p>Enter your pickup and delivery locations. You can add up to 5 pickups and 5 drops for multi-stop loads.</p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">2. Equipment & Details</h4>
                <p>Select the equipment type (Van, Reefer, Flatbed, etc.), enter weight, length, and commodity info.</p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">3. AI Rate Suggestions</h4>
                <p>Once origin, destination, and equipment are filled in, Atlas AI will suggest a competitive rate based on live market data.</p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">4. Boost (Optional)</h4>
                <p>Use credits to feature your load at the top of search results for increased visibility.</p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">5. Set Rate & Post</h4>
                <p>Enter your rate, optionally enable Auction Mode to receive counter-offers, then hit Post Load.</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {isDirty && (
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "hsl(var(--success))" }} />
            <span className="font-medium text-muted-foreground">Draft Saved</span>
          </div>
        )}
      </div>
    </div>
  );
}
